<template>
  <div class="text-cutout-page">
    <div class="page-header">
      <h2><i class="el-icon-scissors"></i> 图片文字工具箱</h2>
      <p>包含 3 个功能：黑字抠图、手写签名、文本转手写体，均可下载 PNG。</p>
    </div>

    <div class="panel">
      <el-tabs v-model="activeTab" type="border-card">
        <el-tab-pane label="黑字抠图（白底透明）" name="cutout">
          <div class="controls">
            <el-upload
              class="uploader"
              action=""
              :show-file-list="false"
              :auto-upload="false"
              accept="image/png,image/jpeg,image/webp"
              :on-change="onFileChange"
            >
              <el-button type="primary" icon="el-icon-upload2">上传图片</el-button>
            </el-upload>

            <div class="threshold-wrap">
              <span>阈值：{{ threshold }}</span>
              <el-slider
                v-model="threshold"
                :min="80"
                :max="245"
                :step="1"
                @input="reprocess"
              />
              <div class="tip">图片偏灰/笔画细时可调低；背景残留时调高。</div>
            </div>

            <el-button
              type="success"
              icon="el-icon-download"
              :disabled="!outputDataUrl"
              @click="downloadByUrl(outputDataUrl, 'text-cutout')"
            >
              下载透明 PNG
            </el-button>
          </div>

          <div class="preview-grid">
            <div class="preview-card">
              <h3>原图</h3>
              <div class="preview-box checker">
                <img v-if="sourceDataUrl" :src="sourceDataUrl" alt="原图" />
                <div v-else class="empty">请先上传图片</div>
              </div>
            </div>

            <div class="preview-card">
              <h3>处理后（透明背景）</h3>
              <div class="preview-box checker">
                <img v-if="outputDataUrl" :src="outputDataUrl" alt="处理后" />
                <div v-else class="empty">暂无结果</div>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="手写签名（鼠标/触摸）" name="draw">
          <div class="controls draw-controls">
            <div class="line-control">
              <span>笔画粗细：{{ drawLineWidth }} px</span>
              <el-slider v-model="drawLineWidth" :min="2" :max="18" />
            </div>
            <el-button icon="el-icon-delete" @click="clearDrawCanvas">清空画布</el-button>
            <el-button
              type="success"
              icon="el-icon-download"
              :disabled="!hasDrawStrokes"
              @click="downloadDrawCanvas"
            >
              下载 PNG
            </el-button>
          </div>
          <div class="draw-canvas-wrap checker">
            <canvas
              ref="drawCanvas"
              class="draw-canvas"
              @mousedown="onDrawStart"
              @mousemove="onDrawMove"
              @mouseup="onDrawEnd"
              @mouseleave="onDrawEnd"
              @touchstart.prevent="onDrawStart"
              @touchmove.prevent="onDrawMove"
              @touchend.prevent="onDrawEnd"
            ></canvas>
          </div>
          <div class="tip">支持鼠标与触摸书写，导出为透明背景 PNG。</div>
        </el-tab-pane>

        <el-tab-pane label="文本转手写体" name="font">
          <div class="controls font-controls">
            <el-input
              v-model="typedText"
              type="textarea"
              :rows="3"
              placeholder="请输入要生成的文字，例如：刘小明"
              @input="renderTypedPreview"
            />
            <div class="font-grid">
              <div>
                <span>字号：{{ typedFontSize }} px</span>
                <el-slider v-model="typedFontSize" :min="36" :max="180" @input="renderTypedPreview" />
              </div>
              <div>
                <span>笔画粗细：{{ typedFontWeight }}</span>
                <el-slider v-model="typedFontWeight" :min="300" :max="900" :step="100" @input="renderTypedPreview" />
              </div>
            </div>
            <el-button type="success" icon="el-icon-download" :disabled="!typedOutputDataUrl" @click="downloadByUrl(typedOutputDataUrl, 'handwrite-text')">
              下载 PNG
            </el-button>
          </div>

          <div class="preview-card">
            <h3>预览（透明背景）</h3>
            <div class="preview-box checker">
              <img v-if="typedOutputDataUrl" :src="typedOutputDataUrl" alt="手写体预览" />
              <div v-else class="empty">请输入文字后生成预览</div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <canvas ref="workCanvas" class="hidden-canvas"></canvas>
    <canvas ref="typedCanvas" class="hidden-canvas"></canvas>
  </div>
</template>

<script>
export default {
  name: 'ImageTextCutout',
  data() {
    return {
      activeTab: 'cutout',
      threshold: 185,
      sourceDataUrl: '',
      outputDataUrl: '',
      sourceImage: null,
      drawLineWidth: 6,
      isDrawing: false,
      hasDrawStrokes: false,
      lastPoint: { x: 0, y: 0 },
      typedText: '',
      typedFontSize: 96,
      typedFontWeight: 500,
      typedOutputDataUrl: ''
    }
  },
  mounted() {
    this.initDrawCanvas()
    this._resizeHandler = () => {
      this.initDrawCanvas()
      this.renderTypedPreview()
    }
    window.addEventListener('resize', this._resizeHandler)
  },
  beforeDestroy() {
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler)
  },
  methods: {
    // ------- 1) 黑字抠图 -------
    onFileChange(file) {
      const raw = file && file.raw
      if (!raw) return
      if (!/^image\//.test(raw.type)) {
        this.$message.warning('请上传图片文件')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        this.sourceDataUrl = String(reader.result || '')
        this.loadImageAndProcess(this.sourceDataUrl)
      }
      reader.readAsDataURL(raw)
    },
    loadImageAndProcess(dataUrl) {
      const img = new Image()
      img.onload = () => {
        this.sourceImage = img
        this.reprocess()
      }
      img.onerror = () => this.$message.error('图片读取失败，请重试')
      img.src = dataUrl
    },
    reprocess() {
      if (!this.sourceImage) return
      const canvas = this.$refs.workCanvas
      if (!canvas) return
      const img = this.sourceImage
      canvas.width = img.naturalWidth || img.width
      canvas.height = img.naturalHeight || img.height
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data
      const t = this.threshold
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i]
        const g = data[i + 1]
        const b = data[i + 2]
        const gray = 0.299 * r + 0.587 * g + 0.114 * b
        if (gray <= t) {
          const alpha = Math.min(255, Math.max(0, Math.round(((t - gray) / t) * 255)))
          data[i] = 0
          data[i + 1] = 0
          data[i + 2] = 0
          data[i + 3] = alpha
        } else {
          data[i + 3] = 0
        }
      }
      ctx.putImageData(imageData, 0, 0)
      this.outputDataUrl = canvas.toDataURL('image/png')
    },

    // ------- 2) 手写签名 -------
    initDrawCanvas() {
      const canvas = this.$refs.drawCanvas
      if (!canvas) return
      const wrap = canvas.parentElement
      const cssWidth = Math.max(320, (wrap && wrap.clientWidth ? wrap.clientWidth : 900) - 2)
      const cssHeight = window.innerWidth <= 768 ? 260 : 320
      const dpr = window.devicePixelRatio || 1
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const old = canvas.toDataURL('image/png')

      canvas.style.width = `${cssWidth}px`
      canvas.style.height = `${cssHeight}px`
      canvas.width = Math.floor(cssWidth * dpr)
      canvas.height = Math.floor(cssHeight * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = '#000'
      ctx.fillStyle = 'transparent'

      if (this.hasDrawStrokes) {
        const img = new Image()
        img.onload = () => ctx.drawImage(img, 0, 0, cssWidth, cssHeight)
        img.src = old
      } else {
        ctx.clearRect(0, 0, cssWidth, cssHeight)
      }
    },
    getCanvasPoint(e, canvas) {
      const rect = canvas.getBoundingClientRect()
      if (e.touches && e.touches[0]) {
        return {
          x: e.touches[0].clientX - rect.left,
          y: e.touches[0].clientY - rect.top
        }
      }
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    },
    onDrawStart(e) {
      const canvas = this.$refs.drawCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      this.isDrawing = true
      const p = this.getCanvasPoint(e, canvas)
      this.lastPoint = p
      ctx.beginPath()
      ctx.moveTo(p.x, p.y)
    },
    onDrawMove(e) {
      if (!this.isDrawing) return
      const canvas = this.$refs.drawCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const p = this.getCanvasPoint(e, canvas)
      ctx.lineWidth = this.drawLineWidth
      ctx.beginPath()
      ctx.moveTo(this.lastPoint.x, this.lastPoint.y)
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
      this.lastPoint = p
      this.hasDrawStrokes = true
    },
    onDrawEnd() {
      this.isDrawing = false
    },
    clearDrawCanvas() {
      const canvas = this.$refs.drawCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight)
      this.hasDrawStrokes = false
    },
    downloadDrawCanvas() {
      const canvas = this.$refs.drawCanvas
      if (!canvas || !this.hasDrawStrokes) return
      this.downloadByUrl(canvas.toDataURL('image/png'), 'hand-signature')
    },

    // ------- 3) 文本转手写体 -------
    renderTypedPreview() {
      const text = (this.typedText || '').trim()
      if (!text) {
        this.typedOutputDataUrl = ''
        return
      }
      const canvas = this.$refs.typedCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const maxWidth = Math.min(1200, Math.max(600, window.innerWidth <= 768 ? window.innerWidth - 24 : 980))
      const padding = 36
      const fontSize = this.typedFontSize
      const lineHeight = Math.round(fontSize * 1.35)
      const fontFamily = `"Segoe Script","KaiTi","STKaiti","Lucida Handwriting","Comic Sans MS","Microsoft YaHei",cursive`
      ctx.font = `${this.typedFontWeight} ${fontSize}px ${fontFamily}`
      const rawLines = text.split(/\n+/g)
      const lines = []
      rawLines.forEach((line) => {
        if (!line) {
          lines.push('')
          return
        }
        let current = ''
        for (const ch of line) {
          const next = current + ch
          if (ctx.measureText(next).width > (maxWidth - padding * 2) && current) {
            lines.push(current)
            current = ch
          } else {
            current = next
          }
        }
        lines.push(current)
      })
      const width = maxWidth
      const height = Math.max(180, padding * 2 + lineHeight * lines.length)
      canvas.width = width
      canvas.height = height
      ctx.clearRect(0, 0, width, height)
      ctx.textBaseline = 'top'
      ctx.fillStyle = '#000'
      ctx.font = `${this.typedFontWeight} ${fontSize}px ${fontFamily}`
      lines.forEach((line, i) => ctx.fillText(line, padding, padding + i * lineHeight))
      this.typedOutputDataUrl = canvas.toDataURL('image/png')
    },

    // ------- 共用下载 -------
    downloadByUrl(url, prefix) {
      if (!url) return
      const a = document.createElement('a')
      a.href = url
      a.download = `${prefix}-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }
}
</script>

<style scoped>
.text-cutout-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 16px 20px 36px;
}
.page-header h2 {
  margin: 0 0 8px;
  font-size: 24px;
  color: #303133;
}
.page-header h2 i {
  margin-right: 8px;
  color: #409eff;
}
.page-header p {
  margin: 0 0 14px;
  color: #606266;
  font-size: 14px;
}
.panel {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  padding: 16px;
}
.panel :deep(.el-tabs__header) {
  margin-bottom: 16px;
}
.controls {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 14px;
  align-items: center;
  margin-bottom: 16px;
}
.draw-controls,
.font-controls {
  grid-template-columns: 1fr auto auto;
}
.font-controls {
  align-items: start;
}
.font-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.line-control span,
.threshold-wrap span,
.font-grid span {
  font-size: 14px;
  color: #303133;
}
.tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}
.preview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.preview-card h3 {
  margin: 0 0 8px;
  font-size: 14px;
  color: #606266;
}
.preview-box {
  min-height: 320px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 8px;
}
.draw-canvas-wrap {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
}
.draw-canvas {
  display: block;
  width: 100%;
  height: 320px;
  background: transparent;
}
.checker {
  background-image:
    linear-gradient(45deg, #f2f2f2 25%, transparent 25%),
    linear-gradient(-45deg, #f2f2f2 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f2f2f2 75%),
    linear-gradient(-45deg, transparent 75%, #f2f2f2 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
}
.preview-box img {
  max-width: 100%;
  max-height: 72vh;
  object-fit: contain;
}
.empty {
  color: #909399;
  font-size: 14px;
}
.hidden-canvas {
  display: none;
}

@media (max-width: 900px) {
  .controls,
  .draw-controls,
  .font-controls {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .font-grid {
    grid-template-columns: 1fr;
  }
  .preview-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .text-cutout-page {
    padding: 12px 12px 24px;
  }
  .panel {
    padding: 12px;
  }
  .panel :deep(.el-tabs__header) {
    overflow: visible;
  }
  .panel :deep(.el-tabs__nav-wrap) {
    overflow: visible;
  }
  .panel :deep(.el-tabs__nav-scroll) {
    overflow: visible;
  }
  .panel :deep(.el-tabs__nav) {
    float: none;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  }
  .panel :deep(.el-tabs__item) {
    flex: 1 1 auto;
    white-space: nowrap;
    padding: 0 10px;
  }
  .preview-box {
    min-height: 220px;
  }
  .draw-canvas {
    height: 260px;
  }
}
</style>
